var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from "react";
import styled, { useTheme } from "styled-components";
import { variant } from "styled-system";
import { includeStyledSystem, sx } from "../util/styledSystem";
import { Stack } from "./Stack";
import { getVariants as getTextStyleVariants } from "./Text";
import { LoadingSpinner, SpinnerSize } from "./LoadingSpinner";
const getVariants = ({ borders, colors: { destructiveButtonColors, iconButtonColors, invertedButtonColors, invertedDarkButtonColors, primaryButtonColors, secondaryButtonColors, tertiaryButtonColors, }, }) => ({
    destructive: {
        // Disabled state has to override hover state
        "&&:disabled": {
            backgroundColor: destructiveButtonColors.disabledBackground,
            borderColor: destructiveButtonColors.disabledBackground,
            color: destructiveButtonColors.disabledText,
            cursor: "not-allowed",
        },
        "&:focus": {
            backgroundColor: destructiveButtonColors.focusBackground,
            borderColor: destructiveButtonColors.focusBackground,
            color: destructiveButtonColors.focusText,
        },
        "&:hover": {
            backgroundColor: destructiveButtonColors.hoverBackground,
            borderColor: destructiveButtonColors.hoverBackground,
            color: destructiveButtonColors.hoverText,
        },
        backgroundColor: destructiveButtonColors.background,
        border: borders[1],
        borderColor: destructiveButtonColors.background,
        color: destructiveButtonColors.text,
        textTransform: "uppercase",
    },
    icon: {
        "&:hover": {
            borderColor: iconButtonColors.hoverBorder,
            textDecoration: "none",
        },
        backgroundColor: iconButtonColors.background,
        border: borders[1],
        borderColor: iconButtonColors.border,
        height: 36,
        width: 36,
    },
    iconWithoutBorder: {
        // Disabled state has to override hover state
        "&&:disabled": {
            color: iconButtonColors.disabled,
            cursor: "not-allowed",
        },
        "&:focus-visible": {
            backgroundColor: iconButtonColors.focusBackground,
        },
        "&:hover": {
            textDecoration: "none",
        },
        backgroundColor: "transparent",
        borderRadius: "50%",
    },
    inverted: {
        // Disabled state has to override hover state
        "&&:disabled": {
            "&:hover": {
                backgroundColor: invertedButtonColors.invertedButtonDisabledBackground,
            },
            backgroundColor: invertedButtonColors.invertedButtonDisabledBackground,
            borderColor: invertedButtonColors.invertedButtonDisabledBorder,
            color: invertedButtonColors.invertedButtonDisabledText,
            cursor: "not-allowed",
        },
        "&:focus": {
            backgroundColor: invertedButtonColors.invertedButtonFocusBackground,
            borderColor: invertedButtonColors.invertedButtonFocusBorder,
            color: invertedButtonColors.invertedButtonFocusText,
        },
        "&:hover": {
            backgroundColor: invertedButtonColors.invertedButtonHoverBackground,
            borderColor: invertedButtonColors.invertedButtonHoverBorder,
            color: invertedButtonColors.invertedButtonHoverText,
        },
        backgroundColor: invertedButtonColors.invertedButtonBackground,
        border: borders[1],
        borderColor: invertedButtonColors.invertedButtonBorder,
        color: invertedButtonColors.invertedButtonText,
        textTransform: "uppercase",
    },
    invertedDark: {
        // Disabled state has to override hover state
        "&&:disabled": {
            "&:hover": {
                backgroundColor: invertedDarkButtonColors.invertedDarkButtonDisabledBackground,
            },
            backgroundColor: invertedDarkButtonColors.invertedDarkButtonDisabledBackground,
            borderColor: invertedDarkButtonColors.invertedDarkButtonDisabledBorder,
            color: invertedDarkButtonColors.invertedDarkButtonDisabledText,
            cursor: "not-allowed",
        },
        "&:focus": {
            backgroundColor: invertedDarkButtonColors.invertedDarkButtonFocusBackground,
            borderColor: invertedDarkButtonColors.invertedDarkButtonFocusBorder,
            color: invertedDarkButtonColors.invertedDarkButtonFocusText,
        },
        "&:hover": {
            backgroundColor: invertedDarkButtonColors.invertedDarkButtonHoverBackground,
            borderColor: invertedDarkButtonColors.invertedDarkButtonHoverBorder,
            color: invertedDarkButtonColors.invertedDarkButtonHoverText,
        },
        backgroundColor: invertedDarkButtonColors.invertedDarkButtonBackground,
        border: borders[1],
        borderColor: invertedDarkButtonColors.invertedDarkButtonBorder,
        color: invertedDarkButtonColors.invertedDarkButtonText,
        textTransform: "uppercase",
    },
    primary: {
        // Disabled state has to override hover state
        "&&:disabled": {
            backgroundColor: primaryButtonColors.disabledBackground,
            borderColor: primaryButtonColors.disabledBackground,
            color: primaryButtonColors.disabledText,
            cursor: "not-allowed",
        },
        "&:focus": {
            backgroundColor: primaryButtonColors.focusBackground,
            borderColor: primaryButtonColors.focusBackground,
            color: primaryButtonColors.focusText,
        },
        "&:hover": {
            backgroundColor: primaryButtonColors.hoverBackground,
            borderColor: primaryButtonColors.hoverBackground,
            color: primaryButtonColors.hoverText,
        },
        backgroundColor: primaryButtonColors.background,
        border: borders[1],
        borderColor: primaryButtonColors.background,
        color: primaryButtonColors.text,
        textTransform: "uppercase",
    },
    secondary: {
        // Disabled state has to override hover state
        "&&:disabled": {
            backgroundColor: secondaryButtonColors.secondaryButtonDisabledBackground,
            borderColor: secondaryButtonColors.secondaryButtonDisabledBorder,
            color: secondaryButtonColors.secondaryButtonDisabledText,
            cursor: "not-allowed",
        },
        "&:focus": {
            backgroundColor: secondaryButtonColors.secondaryButtonFocusBackground,
            borderColor: secondaryButtonColors.secondaryButtonFocusBorder,
            color: secondaryButtonColors.secondaryButtonFocusText,
        },
        "&:hover": {
            backgroundColor: secondaryButtonColors.secondaryButtonHoverBackground,
            borderColor: secondaryButtonColors.secondaryButtonHoverBorder,
            color: secondaryButtonColors.secondaryButtonHoverText,
        },
        backgroundColor: secondaryButtonColors.secondaryButtonBackground,
        border: borders[1],
        borderColor: secondaryButtonColors.secondaryButtonBorder,
        color: secondaryButtonColors.secondaryButtonText,
        textTransform: "uppercase",
    },
    tertiary: {
        // Disabled state has to override hover state
        "&&:disabled": {
            borderColor: tertiaryButtonColors.tertiaryButtonDisabledBorder,
            color: tertiaryButtonColors.tertiaryButtonDisabledText,
            cursor: "not-allowed",
        },
        "&:focus": {
            borderColor: tertiaryButtonColors.tertiaryButtonFocusBorder,
            color: tertiaryButtonColors.tertiaryButtonFocusText,
        },
        "&:hover": {
            borderColor: tertiaryButtonColors.tertiaryButtonHoverBorder,
            color: tertiaryButtonColors.tertiaryButtonHoverText,
        },
        // eslint-disable-next-line -- Increase specificity to ensure there's no border-radius for this variant
        "&&": {
            borderRadius: 0,
        },
        background: "none",
        border: borders[0],
        borderBottom: borders[1],
        borderColor: "transparent",
        color: tertiaryButtonColors.tertiaryButtonText,
        textTransform: "uppercase",
    },
});
export const buttonSizes = {
    compact: {
        borderRadius: "4px",
        fontSize: 0,
        fontWeight: "500",
        padding: "3px 8px",
    },
    icon: {
        fontSize: 0,
        minWidth: 0,
        paddingX: 2,
        paddingY: 2,
    },
    large: {
        borderRadius: "4px",
        fontSize: 1,
        fontWeight: "normal",
        minWidth: "200px",
        padding: "14px 20px",
    },
    medium: {
        borderRadius: "4px",
        fontSize: 1,
        fontWeight: "normal",
        minWidth: "170px",
        padding: "10px 20px",
    },
    noPadding: {
        borderRadius: 0,
        fontSize: 1,
        fontWeight: 600,
        padding: 0,
    },
    small: {
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: "normal",
        minWidth: "50px",
        padding: "6px 12px",
    },
    tertiary: {
        borderRadius: 0,
        fontSize: 1,
        fontWeight: 600,
        padding: 0,
        paddingBottom: 1,
        paddingTop: 1,
    },
};
const DEFAULT_VARIANT = "primary";
const StyledButton = styled.button `
  ${({ theme }) => ({
    alignItems: "center",
    border: theme.borders[0],
    borderRadius: theme.radii[1],
    color: theme.colors.primaryButtonColors.default,
    cursor: "pointer",
    display: "inline-flex",
    fontFamily: theme.fonts.main,
    fontSize: 1,
    fontWeight: "bold",
    justifyContent: "center",
    letterSpacing: "normal",
    lineHeight: "normal",
})}

  ${({ theme, variant = DEFAULT_VARIANT }) => (Object.assign({}, getVariants(theme)[variant]))};
  ${variant({ prop: "size", variants: buttonSizes })}
  ${({ theme, textStyle }) => (textStyle ? Object.assign({}, getTextStyleVariants(theme)[textStyle]) : undefined)}
  ${includeStyledSystem()}
  ${sx}
`;
export const Button = React.forwardRef((props, ref) => {
    var _a, _b;
    const { loading, variant = DEFAULT_VARIANT } = props, restOfProps = __rest(props, ["loading", "variant"]);
    const theme = useTheme();
    // Worst case scenario where we don't have a useful value for `data-trackid`
    let trackId = "Button";
    if (props["data-trackid"]) {
        trackId = props["data-trackid"];
    }
    else if (typeof props.children === "string") {
        trackId = props.children;
    }
    if (loading) {
        // Make the spinner color the same as the button's text color
        const spinnerColor = (_b = (_a = getVariants(theme)[variant]["&&:disabled"]) === null || _a === void 0 ? void 0 : _a.color) !== null && _b !== void 0 ? _b : theme.colors.primaryButtonColors.disabledText;
        return (React.createElement(StyledButton, Object.assign({}, restOfProps, { variant: variant, color: props.color, disabled: true, ref: ref, "data-trackid": trackId }),
            React.createElement(LoadingSpinner, { size: SpinnerSize.BUTTON, spinnerColor: spinnerColor })));
    }
    return (React.createElement(StyledButton, Object.assign({}, restOfProps, { variant: variant, color: props.color, ref: ref, "data-trackid": trackId })));
});
Button.defaultProps = {
    "data-testid": "button",
    disabled: false,
    size: "medium",
    type: "button",
    variant: "primary",
};
export const ButtonGroup = (props) => React.createElement(Stack, Object.assign({ direction: "horizontal", alignItems: "flex-start" }, props));
